/**
 * aiApi.js
 * ========
 * Thin client for the /ai-options backend endpoint.
 * Calls the Flask dev server or Lambda (configured via REACT_APP_INFERENCE_URL).
 */

const BASE_URL =
  process.env.REACT_APP_INFERENCE_URL || 'http://localhost:5000';

/**
 * generateAiOptions
 * -----------------
 * POST /ai-options → returns 3 short AAC response strings.
 *
 * @param {object} params
 * @param {string} params.caregiverText  — what the caregiver said
 * @param {string} [params.context]      — recent conversation history (optional)
 * @returns {Promise<string[]>}           — array of exactly 3 response strings
 * @throws {Error}                        — on network failure or timeout (>10 s)
 */
export async function generateAiOptions({ caregiverText, context = '', regenerate = false }) {
  const controller = new AbortController();
  const timeout    = setTimeout(() => controller.abort(), 55_000);

  try {
    const res = await fetch(`${BASE_URL}/ai-options`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      signal:  controller.signal,
      body:    JSON.stringify({ caregiverText, context, regenerate }),
    });

    if (!res.ok) {
      throw new Error(`/ai-options request failed with status ${res.status}`);
    }

    const data = await res.json();
    return Array.isArray(data.responses) ? data.responses : [];
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('AI request timed out — please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}
